import { useEffect, useRef, useState } from 'react';
import { useIsAnalyticsDataFetching } from './useIsAnalyticsDataFetching';

const SHOW_DELAY_MS = 150;
const MIN_VISIBLE_MS = 350;

/**
 * Drives the inline "Loading…" hint in the analytics header: smoothed with a
 * show-delay and a minimum visible duration so quick fetches never flash.
 */
export function useAnalyticsFetchingHint(): boolean {
  const isFetching = useIsAnalyticsDataFetching();
  const [visible, setVisible] = useState(false);
  const shownAtRef = useRef<number | null>(null);

  useEffect(() => {
    let showTimer: number | undefined;
    let hideTimer: number | undefined;

    if (isFetching && !visible) {
      showTimer = window.setTimeout(() => {
        shownAtRef.current = performance.now();
        setVisible(true);
      }, SHOW_DELAY_MS);
    } else if (!isFetching && visible) {
      const elapsed =
        shownAtRef.current != null
          ? performance.now() - shownAtRef.current
          : MIN_VISIBLE_MS;
      const remaining = Math.max(MIN_VISIBLE_MS - elapsed, 0);
      hideTimer = window.setTimeout(() => {
        setVisible(false);
        shownAtRef.current = null;
      }, remaining);
    }

    return () => {
      if (showTimer !== undefined) {
        window.clearTimeout(showTimer);
      }
      if (hideTimer !== undefined) {
        window.clearTimeout(hideTimer);
      }
    };
  }, [isFetching, visible]);

  return visible;
}
